import { cn } from '@amore/ui/cn'
import { Badge } from '@amore/ui/components/badge'
import { Button, buttonStyles } from '@amore/ui/components/button'
import { Card } from '@amore/ui/components/card'
import { Page, PageBody } from '@amore/ui/components/page'
import { Link } from '@tanstack/react-router'
import { ArrowRight, CalendarDays, Mail, MapPin, PencilLine } from 'lucide-react'
import { type Ref, useRef, useState } from 'react'
import { daysBetween, formatDate, plural, TODAY } from '@/lib/format'
import { useCanEdit, useWedding } from '@/mock/store'
import { EmptyState } from '@/patterns/empty-state'
import photoCouple from './assets/photo-couple.svg'
import { newReplies } from './model'
import { OverviewNumbers, OverviewPrompts } from './sections'
import { OverviewTasks } from './tasks'
import { WeddingIdentityDialog } from './wedding-identity-dialog'

export function OverviewScreen() {
  const canEdit = useCanEdit()
  const [identityOpen, setIdentityOpen] = useState(false)
  const editButton = useRef<HTMLButtonElement>(null)
  const closeIdentity = () => {
    setIdentityOpen(false)
    requestAnimationFrame(() => editButton.current?.focus({ preventScroll: true }))
  }

  return (
    <Page>
      <h1 className="sr-only">Přehled svatby</h1>
      <PageBody className="gap-6 pt-0">
        <WeddingIntro editButton={editButton} onEdit={() => setIdentityOpen(true)} />
        <OverviewNumbers />
        <WeddingNews />
        <OverviewTasks />
        <OverviewPrompts />
      </PageBody>
      {identityOpen && canEdit ? <WeddingIdentityDialog onClose={closeIdentity} /> : null}
    </Page>
  )
}

function WeddingNews() {
  const data = useWedding()
  const replies = newReplies(data)
  if (!replies.length) return null
  const names = replies
    .slice(0, 3)
    .map((reply) => reply.name)
    .join(' · ')
  return (
    <section aria-labelledby="overview-news" className="space-y-3">
      <h2 id="overview-news" className="font-heading text-2xl leading-tight tracking-tight">
        Co je nového
      </h2>
      <Card
        padding="none"
        className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex min-w-0 items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent text-action">
            <Mail className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold">
                {replies.length}{' '}
                {plural(replies.length, 'nová odpověď', 'nové odpovědi', 'nových odpovědí')} od
                hostů
              </h3>
              <Badge tone="accent">Čeká na kontrolu</Badge>
            </div>
            <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
              {names}
              {replies.length > 3 ? ` a ${replies.length - 3} další` : ''}
            </p>
          </div>
        </div>
        <Link
          to="/guests/replies"
          className={cn(
            buttonStyles({ appearance: 'outline', tone: 'neutral', size: 'sm' }),
            'shrink-0 self-start sm:self-center',
          )}
        >
          Zkontrolovat odpovědi
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </Card>
    </section>
  )
}

function WeddingIntro({
  onEdit,
  editButton,
}: {
  onEdit: () => void
  editButton: Ref<HTMLButtonElement>
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const { brideName, groomName, date } = data.identity
  const days = date ? daysBetween(TODAY, date) : null
  const venue = data.suppliers.find(
    (supplier) =>
      supplier.category === 'venue' &&
      (supplier.status === 'agreed' || supplier.status === 'booked'),
  )
  if (!brideName && !groomName)
    return (
      <EmptyState
        icon={<img src={photoCouple} alt="" className="h-28 w-32" />}
        title="Tady začíná váš společný den"
        description="Doplňte jména a datum svatby. Pak můžete pokračovat tím, co je pro vás důležité."
        actions={
          canEdit ? (
            <Button ref={editButton} onClick={onEdit}>
              Doplnit údaje o svatbě
            </Button>
          ) : undefined
        }
      />
    )
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="grid sm:grid-cols-[minmax(0,1fr)_11rem]">
        <div className="flex items-center gap-3 bg-panel-accent p-4 sm:gap-5 sm:px-6 sm:py-5">
          <img src={photoCouple} alt="" className="h-auto w-20 shrink-0 sm:w-28" />
          <div className="min-w-0 flex-1 space-y-2.5">
            <div className="flex items-center gap-2">
              <h2 className="min-w-0 break-words font-heading text-2xl leading-tight tracking-tight sm:text-3xl">
                {brideName}
                {brideName && groomName ? <span className="px-2 text-action"> & </span> : null}
                {groomName}
              </h2>
              {canEdit ? (
                <Button
                  ref={editButton}
                  appearance="ghost"
                  tone="neutral"
                  size="icon"
                  className="size-8 shrink-0"
                  aria-label="Upravit údaje o svatbě"
                  onClick={onEdit}
                >
                  <PencilLine className="size-3.5" aria-hidden="true" />
                </Button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-foreground/80 sm:text-sm">
              <span className="flex items-center gap-1.5">
                <CalendarDays className="size-3.5 shrink-0 text-action" aria-hidden="true" />
                {date ? formatDate(date, 'long') : 'Datum zatím nevybrané'}
              </span>
              {venue ? (
                <Link
                  to={`/suppliers/${venue.id}`}
                  className="flex items-center gap-1.5 underline-offset-4 hover:underline"
                >
                  <MapPin className="size-3.5 shrink-0 text-action" aria-hidden="true" />
                  {venue.name}
                </Link>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center border-t border-border px-4 py-3 text-center sm:border-t-0 sm:border-l sm:py-5">
          <WeddingCountdown days={days} />
        </div>
      </div>
    </Card>
  )
}

function WeddingCountdown({ days }: { days: number | null }) {
  if (days === null)
    return <p className="text-xs leading-5 text-muted-foreground">Těšíme se na váš den</p>
  if (days <= 0)
    return (
      <p className="font-heading text-xl text-action">
        {days === 0 ? 'Dnes je váš den!' : 'Jste svoji!'}
      </p>
    )
  return (
    <div>
      <p className="font-heading text-4xl leading-none tracking-tight text-action tabular-nums sm:text-5xl">
        {days}
      </p>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {plural(days, 'den', 'dny', 'dní')} do svatby
      </p>
    </div>
  )
}
